import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectIsPaused } from "@app/player/playerSlice";
import useKeydown from "@hooks/useKeydown";
import { selectCtrl, setIsPlayerOpen, setCtrl } from "@app/global";
import SvgSettings from "@assets/icons/SvgSettings";
import PlaybackActions from "./PlaybackActions";
import ControlSettings from "./ControlSettings";
import SvgNextEpisode from "@assets/icons/SvgNextEpisode";
import { useMovieInfo } from "@context/movieInfoContext";
import { useTranslation } from "react-i18next";
import PreviewImages from "@components/live/components/PreviewImages";
import ProgressBar from "./ProgressBar";

import "@styles/components/vodControl.scss";
import { formatTime } from "@utils/util";

let hideControlsTimer = null;

export default function VodControls({
  durationRef,
  currentTimeRef,
  refVideo,
  play,
  pause,
  title,
  onBack,
  commitSeek,
  duration: durationProp,
}) {
  const dispatch = useDispatch();
  const isPaused = useSelector(selectIsPaused);
  const ctrl = useSelector(selectCtrl);
  const { t } = useTranslation();
  const { isLastEpisode, movieInfo } = useMovieInfo();

  const [hideControls, setHideControls] = useState(false);
  const [activeCtrl, setActiveCtrl] = useState("top");
  const [topActiveIndex, setTopActiveIndex] = useState(1);
  const [bottomActiveIndex, setBottomActiveIndex] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seekTime, setSeekTime] = useState(null);

  const controlsRef = useRef(null);
  const animationFrameRef = useRef(null);
  const seekDebounceRef = useRef(null);
  const wasPlayingRef = useRef(false);

  const showControl = () => {
    setHideControls(false);
    clearTimeout(hideControlsTimer);
    hideControlsTimer = setTimeout(() => {
      setHideControls(true);
      setShowSettings(false);
      dispatch(setCtrl("vodCtrl"));
    }, 3000);
  };

  const imitateTimeUpdate = (time, dur) => {
    if (currentTimeRef.current) {
      currentTimeRef.current.innerHTML = formatTime(time);
    }
    if (durationRef.current) {
      durationRef.current.innerHTML = formatTime(dur);
    }
  };

  const updateProgress = useCallback(() => {
    let time = 0;
    let dur = 0;

    if (window.Android) {
      time = window.Android.getCurrentTime() || 0;
      dur = window.Android.getVideoDuration() || 0;
    } else if (refVideo.current) {
      time = refVideo.current.currentTime || 0;
      dur = refVideo.current.duration || 0;
    }

    setCurrentTime(time);
    setDuration(dur);

    animationFrameRef.current = requestAnimationFrame(updateProgress);
  }, [refVideo]);

  useEffect(() => {
    showControl();
    animationFrameRef.current = requestAnimationFrame(updateProgress);

    const handleMouseMove = () => {
      showControl();
    };

    const playerElement = refVideo.current?.parentElement;
    if (playerElement) {
      playerElement.addEventListener("mousemove", handleMouseMove);
    }

    return () => {
      if (playerElement) {
        playerElement.removeEventListener("mousemove", handleMouseMove);
      }
      clearTimeout(hideControlsTimer);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }
    };
  }, [refVideo, updateProgress]);

  useEffect(() => {
    if (seekTime !== null && Math.abs(currentTime - seekTime) < 2) {
      setSeekTime(null);
    }
  }, [currentTime, seekTime]);

  const handleSeek = (direction, seconds = 10) => {
    const currentPos = seekTime !== null ? seekTime : currentTime;
    const maxDuration = durationProp || duration;
    const newTime =
      direction === "forward"
        ? Math.min(currentPos + seconds, maxDuration)
        : Math.max(currentPos - seconds, 0);

    if (seekTime === null) {
      wasPlayingRef.current = !isPaused;
    }

    pause();

    setSeekTime(newTime);

    clearTimeout(seekDebounceRef.current);
    seekDebounceRef.current = setTimeout(() => {
      if (commitSeek) {
        commitSeek(newTime);
      }
      setTimeout(() => {
        if (wasPlayingRef.current) play();
      }, 500);
    }, 1500);

    showControl();
  };

  const handleSettingsClick = () => {
    setShowSettings(true);
    dispatch(setCtrl("settings"));
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
    dispatch(setCtrl("vodCtrl"));
  };

  useKeydown({
    isActive: ctrl === "vodCtrl",
    left: () => {
      if (activeCtrl === "top") {
        setTopActiveIndex((prev) => Math.max(0, prev - 1));
      } else {
        setBottomActiveIndex((prev) => Math.max(0, prev - 1));
      }
      showControl();
    },
    right: () => {
      if (activeCtrl === "top") {
        setTopActiveIndex((prev) => Math.min(2, prev + 1));
      } else {
        const maxIndex = !isLastEpisode ? 1 : 0;
        setBottomActiveIndex((prev) => Math.min(maxIndex, prev + 1));
      }
      showControl();
    },
    down: () => {
      setActiveCtrl("bottom");
      if (movieInfo.type === "tv_show") {
        setBottomActiveIndex(0);
      } else {
        setBottomActiveIndex(1);
      }
      showControl();
    },
    up: () => {
      setActiveCtrl("top");
      setTopActiveIndex(1);
      showControl();
    },
    ok: () => {
      if (activeCtrl === "top") {
        if (topActiveIndex === 0) {
          if (window.Android) {
            const time = window.Android.getCurrentTime();
            const dur = window.Android.getVideoDuration();
            imitateTimeUpdate(time, dur);
          } else {
            imitateTimeUpdate(
              refVideo.current?.currentTime,
              refVideo.current?.duration
            );
          }
          handleSeek("backward");
        } else if (topActiveIndex === 1) isPaused ? play() : pause();
        else if (topActiveIndex === 2) {
          if (window.Android) {
            const time = window.Android.getCurrentTime();
            const dur = window.Android.getVideoDuration();
            imitateTimeUpdate(time, dur);
          } else {
            imitateTimeUpdate(
              refVideo.current?.currentTime,
              refVideo.current?.duration
            );
          }
          handleSeek("forward");
        }
      } else {
        if (bottomActiveIndex === 0 && !isLastEpisode) {
          handleNextEpisode();
        } else {
          handleSettingsClick();
        }
      }
      showControl();
    },
    back: () => {
      onBack();
      dispatch(setIsPlayerOpen(false));
      dispatch(setCtrl("movieInfo"));
    },
  });

  const handleNextEpisode = () => {
    document.dispatchEvent(new Event("next-episode"));
  };

  const handleProgressChange = (value) => {
    if (seekTime === null) {
      wasPlayingRef.current = !isPaused;
    }

    pause();

    setSeekTime(value);

    clearTimeout(seekDebounceRef.current);
    seekDebounceRef.current = setTimeout(() => {
      if (commitSeek) {
        commitSeek(value);
      }
      setTimeout(() => {
        if (wasPlayingRef.current) play();
      }, 500);
    }, 1500);
  };

  return (
    <>
      <div
        className={`vod-control${hideControls ? " hide" : ""}`}
        ref={controlsRef}
      >
        <div className="vod-info">
          <h2 className="vod-title">
            {movieInfo.translations ? movieInfo.translations.name : title}
          </h2>
        </div>

        <div className="playback-actions_wrapper">
          <PlaybackActions
            isPaused={isPaused}
            activeIndex={activeCtrl === "top" ? topActiveIndex : -1}
            onSeek={handleSeek}
            onPlayPause={() => (isPaused ? play() : pause())}
          />
        </div>

        <div className="progres-field">
          <ProgressBar
            value={seekTime !== null ? seekTime : currentTime}
            duration={durationProp || duration}
            onChange={handleProgressChange}
          />
          <div className="vod-actions_wrapper">
            <div className="vod-ctrl_btns_right">
              {!isLastEpisode && movieInfo.type === "tv_show" && (
                <button
                  className={`vod-ctrl_btn next-episode-btn${
                    activeCtrl === "bottom" && bottomActiveIndex === 0
                      ? " active"
                      : ""
                  }`}
                  onClick={handleNextEpisode}
                >
                  <SvgNextEpisode />
                  <span>{t("Next episode")}</span>
                </button>
              )}
              <button
                className={`vod-ctrl_btn settings-btn${
                  activeCtrl === "bottom" &&
                  (!isLastEpisode
                    ? bottomActiveIndex === 1
                    : bottomActiveIndex === 0)
                    ? " active"
                    : ""
                }`}
                onClick={handleSettingsClick}
              >
                <SvgSettings />
              </button>
            </div>
          </div>
        </div>

        <PreviewImages onTimeChange={handleProgressChange} />
      </div>

      {showSettings && (
        <ControlSettings
          isVisible={showSettings}
          onClose={handleCloseSettings}
          showControl={showControl}
        />
      )}
    </>
  );
}
